/**
 * useCustomerPortal — Mutation hook for the `customer-portal-session` CF
 * Worker handler.
 *
 * The handler creates a Polar "customer portal session" and returns the
 * hosted portal URL. Callers typically `Linking.openURL(portal_url)` on
 * success.
 *
 * DTO contract (2026-04-18):
 * Uses `fetchJsonValidated(CustomerPortalSessionResultDto, ...)` so server
 * responses are validated against the shared contract.
 *
 * Wave 4B (DTO migration): created alongside the handler DTO migration.
 * Existing call site (`apps/mobile/app/(tabs)/settings.tsx`) uses
 * `authenticatedFetch` directly; new call sites can use this hook for the
 * typed, schema-validated surface.
 */
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import {
  CustomerPortalSessionResultDto,
  type CustomerPortalSessionResult,
} from '@dramadetector/shared';
import { getEdgeFunctionUrl, getAuthHeaders } from '../lib/api-url';

export interface UseCustomerPortalReturn {
  /** Trigger session creation — resolves with `{ portal_url }`. */
  openPortal: () => Promise<CustomerPortalSessionResult>;
  /** True while the mutation is in-flight. */
  isOpening: boolean;
  /** Last error, or null. */
  error: Error | null;
}

export function useCustomerPortal(): UseCustomerPortalReturn {
  const { accessToken } = useAuth();

  const mutation = useMutation({
    mutationFn: async (): Promise<CustomerPortalSessionResult> => {
      if (!accessToken) throw new Error('Not authenticated');

      return await fetchJsonValidated(
        CustomerPortalSessionResultDto,
        getEdgeFunctionUrl('customer-portal-session'),
        {
          method: 'POST',
          headers: { ...getAuthHeaders(accessToken) },
        },
      );
    },
  });

  return {
    openPortal: mutation.mutateAsync,
    isOpening: mutation.isPending,
    error: mutation.error as Error | null,
  };
}
